import type { FigmaVariablesExportsAPIData } from "@/src/types/index.ts";
import type { ZeytalCLIPureConfig } from "@zeytal/kit";
import type { ZeytalApi } from "@zeytal/types";
import type { OptionValues } from "commander";

import * as path from "node:path";

import { projectTokenHeaderName } from "@zeytal/kit/api";
import {
  makeVariablesStructure,
  transformVariablesValues,
  translateVariables,
} from "@zeytal/tokens";
import { cyan, magenta } from "colorette";
import { Command } from "commander";

import { errorMessages } from "@/src/utils/errors.ts";
import { refreshFolder, writeFile } from "@/src/utils/file-system.ts";
import { getConfig } from "@/src/utils/get-config.ts";
import { getCWD } from "@/src/utils/get-cwd.ts";
import { getZeytalApiBaseUrl } from "@/src/utils/get-zeytal-api-base-url.ts";
import { icons } from "@/src/utils/icons.ts";
import { logger } from "@/src/utils/logger.ts";

const variablesActionCore = async (
  commandOptions: OptionValues,
  zeytalConfig: ZeytalCLIPureConfig,
) => {
  logger.log(`${magenta(icons.leftHalfCircle)} Variables fetching started...`);

  logger.spinner().start("Assessing your configuration parameters...");

  const zeytalAPIBaseUrl = getZeytalApiBaseUrl();

  logger.debug("Getting user’s zeytal project data and figma files list.");

  const figmaVariablesAPIRes = await fetch(
    `${zeytalAPIBaseUrl}/api/variables`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        [projectTokenHeaderName]: zeytalConfig.projectToken ?? "",
      },
    },
  ).catch(() => {
    logger.spinner().fail();
    throw errorMessages.zeytalApiError500();
  });

  const figmaVariablesAPIData =
    (await figmaVariablesAPIRes.json()) as ZeytalApi.ApiResponse<FigmaVariablesExportsAPIData>;

  if (!figmaVariablesAPIData || !figmaVariablesAPIData.success) {
    logger.spinner().fail();
    throw figmaVariablesAPIData.message;
  }

  if (!figmaVariablesAPIData.data || figmaVariablesAPIData.data === null) {
    logger.spinner().fail();
    throw errorMessages.zeytalApiNoData();
  }

  logger.spinner().succeed();

  logger.spinner().start("Processing your Figma variables exports data...");

  logger.debug("Structuring Figma variables.");
  const structuredVariables = makeVariablesStructure(
    figmaVariablesAPIData.data.figmaVariablesExports
      .map((e) => e.variablesExportData)
      .filter((e) => e !== null),
    zeytalConfig,
  );

  logger.spinner().succeed();

  logger.spinner().start("Remodelling your variables values...");

  logger.debug("Transforming Figma variables values.");
  const transformedVariables = transformVariablesValues(
    structuredVariables,
    zeytalConfig,
  );

  logger.spinner().succeed();

  logger
    .spinner()
    .start(
      `Translating your variables into ${[
        ...new Set(zeytalConfig.variables.formats.targets),
      ]
        .map((t) => cyan(t))
        .join(", ")}.`,
    );

  logger.debug("Translating Figma variables.");
  const variablesFiles = translateVariables(transformedVariables, zeytalConfig);

  logger.spinner().succeed();

  logger.spinner().succeed("Variables remodelling and translation done.");

  logger
    .spinner()
    .start(
      `Writing variables files inside ${cyan(zeytalConfig.variables.directoryPath)}...`,
    );

  logger.debug("Refreshing variables directory.");
  await refreshFolder(
    path.join(commandOptions.cwd, zeytalConfig.variables.directoryPath),
  );

  // Writing variables files
  Object.entries(variablesFiles).forEach(([fileName, fileContent]) => {
    logger.debug("Writing variables file.");
    const writePath = path.join(
      commandOptions.cwd,
      zeytalConfig.variables.directoryPath,
      fileName,
    );
    writeFile(writePath, fileContent);
  });

  logger
    .spinner()
    .succeed(
      `All variables files written to ${cyan(zeytalConfig.variables.directoryPath)}`,
    );
};

export const variablesCmd = new Command()
  .name("variables")
  .alias("vars")
  .description("extract and transform Figma variables from your Figma files")
  .action(async (_, command) => {
    logger.break();

    const optsWithGlobals = command.optsWithGlobals();
    logger.setDebugModeTo(optsWithGlobals.debug);

    const options = { ...optsWithGlobals, cwd: getCWD(optsWithGlobals.cwd) };

    logger.debug("Getting user’s zeytal CLI config.");
    const zeytalConfig = await getConfig(options.cwd);

    try {
      await variablesActionCore(options, zeytalConfig);
    } catch (errorMessage) {
      logger.error(errorMessage);
      logger.break();
      process.exit(1);
    }
  });
